/**
 * KAN-958: скрытые столбцы таблицы CRM поверх настроек пользователя
 * (ui_settings.crm_table.hidden_columns). Компоненты только рисуют: скрыть,
 * вернуть и "Показать все" идут сюда, правило списка - в lib/crmTableColumns.
 */
import { useCallback, useMemo } from 'react';
import {
  hiddenColumnSet,
  showAllColumns,
  withColumnVisible,
  type CrmTableColumn,
} from '@/lib/crmTableColumns';
import { useUiSettings } from './useUiSettings';

const NO_HIDDEN: readonly string[] = [];

export interface CrmTableColumnsState {
  columns: readonly CrmTableColumn[];
  /** Скрытые среди текущих столбцов. */
  hidden: ReadonlySet<string>;
  hiddenCount: number;
  hide: (code: string) => void;
  setVisible: (code: string, visible: boolean) => void;
  showAll: () => void;
}

export function useCrmTableColumns(columns: readonly CrmTableColumn[]): CrmTableColumnsState {
  const { settings, update } = useUiSettings();
  const stored = settings.crm_table?.hidden_columns ?? NO_HIDDEN;
  const hidden = useMemo(() => hiddenColumnSet(columns, stored), [columns, stored]);

  const save = useCallback(
    (next: string[]) => update({ crm_table: { hidden_columns: next } }),
    [update]
  );
  const setVisible = useCallback(
    (code: string, visible: boolean) => {
      const column = columns.find((c) => c.code === code);
      if (!column || column.required) return;
      save(withColumnVisible(stored, code, visible));
    },
    [columns, stored, save]
  );
  const hide = useCallback((code: string) => setVisible(code, false), [setVisible]);
  const showAll = useCallback(() => save(showAllColumns(stored, columns)), [stored, columns, save]);

  return { columns, hidden, hiddenCount: hidden.size, hide, setVisible, showAll };
}
